/**
 * Module dependencies.
 */

import { Image, Type } from 'src/components/core';
import { ListItem as ListItemProps } from 'src/types/technologies';
import { ReactElement, forwardRef, useEffect } from 'react';
import { color, media, units, useBreakpoint } from '@untile/react-components';
import { ifProp, prop, theme } from 'styled-tools';
import { useInView } from 'react-intersection-observer';
import { useSectionsNavigation } from 'src/context/sections-navigation/context';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  bgColor?: string;
  className?: string;
  imageUrl: string;
  isReverse?: boolean;
  list: ListItemProps[];
  name?: string;
  title: string;
};

/**
 * `Wrapper` styled compon. ent.
 */

const Wrapper = styled.section<{ bgColor?: string }>`
  background-color: ${prop('bgColor', color('grey50'))};
  padding: ${units(6)} 0 ${units(11)};
  position: relative;

  ${media.min('md')`
    padding: ${units(18)} 0 ${units(13.5)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<{ isReverse: boolean }>`
  display: grid;
  grid-template-areas:
    '. title .'
    '. image .'
    '. list  .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title . image .'
      '. list  . image .';
    grid-template-columns: 2fr 4fr ${theme('grid.columnsGutter')}px 5.3fr 0.7fr;
    grid-template-rows: max-content 1fr;

    ${ifProp(
      'isReverse',
      css`
        grid-template-areas:
          '. image . . title .'
          '. image . . list  .';
        grid-template-columns: 0.7fr 5.3fr 1fr ${theme('grid.columnsGutter')}px 4fr 1fr;
      `
    )}
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  color: ${color.transparentize('black', 0.2)};
  grid-area: title;
  margin-bottom: ${units(3)};

  ${media.min('md')`
    margin-bottom: ${units(8)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  margin: 0 auto ${units(6)};
  position: relative;

  ${media.max('md')`
    max-width: ${units(60)};
    width: 100%;
  `}

  ${media.min('md')`
    margin: 0 auto;
  `}
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: list;
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.div`
  margin-bottom: ${units(4)};

  ${media.min('md')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `ItemTitle` styled component.
 */

const ItemTitle = styled(Type.H4)`
  margin-bottom: ${units(1)};
`;

/**
 * `TextImageSection` component.
 */

const TextImageSection = (props: Props, sectionRef: any): ReactElement => {
  const {
    bgColor,
    className,
    imageUrl,
    isReverse = false,
    list,
    name,
    title
  } = props;

  const isMobile = useBreakpoint('md', 'max');
  const { onSetCurrentSection } = useSectionsNavigation();
  const [ref, inView] = useInView({ threshold: 0.5 });
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  useEffect(() => {
    if (inView && name) {
      onSetCurrentSection(name);
    }
  }, [inView, name, onSetCurrentSection]);

  return (
    <Wrapper
      bgColor={bgColor}
      className={className}
      ref={sectionRef}
    >
      <StyledContainer ref={ref}>
        <Grid isReverse={isReverse}>
          <Title {...animProps}>{title}</Title>

          <ImageWrapper
            {...(!isMobile && {
              'data-scroll': true,
              'data-scroll-speed': 1
            })}
            {...(isMobile && {
              'data-scroll': 'fade-in-up-small',
              'data-scroll-animation-delay': '300',
              'data-scroll-offset': '20%'
            })}
          >
            <Image
              alt={title}
              height={763}
              layout={'intrinsic'}
              src={imageUrl}
              style={{ objectFit: 'contain' }}
              width={640}
            />
          </ImageWrapper>

          <List>
            {map(list, ({ item, key }) => (
              <ListItem
                key={key}
                {...animProps}
              >
                <ItemTitle>{item.title}</ItemTitle>

                <Type.Paragraph>{item.description}</Type.Paragraph>
              </ListItem>
            ))}
          </List>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `TextImageSection` component.
 */

export default forwardRef(TextImageSection);
